export type TPowerDialerSelectionType = "personal" | "team" | "sequence"

const STORAGE_KEY = "power_dialer_selection_type_by_id_v1"

let selectionTypeByIdCache: Record<string, TPowerDialerSelectionType> | null = null

function isSelectionType(value: unknown): value is TPowerDialerSelectionType {
  return value === "personal" || value === "team" || value === "sequence"
}

function readSelectionTypeById() {
  if (selectionTypeByIdCache) {
    return selectionTypeByIdCache
  }

  if (typeof window === "undefined") {
    selectionTypeByIdCache = {}
    return selectionTypeByIdCache
  }

  try {
    const rawValue = localStorage.getItem(STORAGE_KEY)
    if (!rawValue) {
      selectionTypeByIdCache = {}
      return selectionTypeByIdCache
    }

    const parsed = JSON.parse(rawValue) as Record<string, unknown>
    const normalizedEntries = Object.entries(parsed).filter(([, value]) => isSelectionType(value)) as Array<[string, TPowerDialerSelectionType]>
    selectionTypeByIdCache = Object.fromEntries(normalizedEntries)
    return selectionTypeByIdCache
  } catch {
    selectionTypeByIdCache = {}
    return selectionTypeByIdCache
  }
}

function persistSelectionTypeById(nextValue: Record<string, TPowerDialerSelectionType>) {
  if (typeof window === "undefined") {
    return
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextValue))
  } catch {
    // Ignore local storage write failures.
  }
}

export function rememberPowerDialerSelectionType({ targetId, targetType }: { targetId: number; targetType: TPowerDialerSelectionType }) {
  if (!Number.isFinite(targetId) || targetId <= 0) {
    return
  }

  const currentValue = readSelectionTypeById()
  const key = `${targetId}`
  if (currentValue[key] === targetType) {
    return
  }

  const nextValue = {
    ...currentValue,
    [key]: targetType,
  }

  selectionTypeByIdCache = nextValue
  persistSelectionTypeById(nextValue)
}

export function getRememberedPowerDialerSelectionType(targetId: number | null | undefined): TPowerDialerSelectionType | null {
  if (!targetId || targetId <= 0) {
    return null
  }

  const currentValue = readSelectionTypeById()
  return currentValue[`${targetId}`] ?? null
}
